import React from 'react'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import {
  Box,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { Ellipse, EllipseProps, Icon } from 'src/components/atoms'
import { VIEWABLE_CONTRACT_TYPE_AND_REGISTER_ORG_CODES } from 'src/domain/org'
import { tenantStatusMap } from 'src/domain/tenant'
import { TenantStatusEnum } from 'src/slices/services/api'
import style from './styles.module.scss'

type Status = {
  [status: string]: {
    title: string
    ellipseColor: EllipseProps['color']
    color: string
  }
}

const statuses: Status = {
  ready: {
    title: tenantStatusMap[TenantStatusEnum.Ready].title,
    ellipseColor: tenantStatusMap[TenantStatusEnum.Ready].color,
    color: 'black',
  },
  not_ready: {
    title: tenantStatusMap[TenantStatusEnum.NotReady].title,
    ellipseColor: tenantStatusMap[TenantStatusEnum.NotReady].color,
    color: 'vermilion',
  },
  closed: {
    title: tenantStatusMap[TenantStatusEnum.Closed].title,
    ellipseColor: tenantStatusMap[TenantStatusEnum.Closed].color,
    color: 'unavailable',
  },
}

export type TenantListItemProps = {
  storeName?: string
  tenantName: string
  tenantCode: string
  to: string
  status: 'ready' | 'not_ready' | 'closed'
  trainedAt?: string
  orgCode?: string
  infoUpdatedAt?: string
  contractorCode?: string
  section?: string
  representativeRegisterNumber?: string
} & React.StyledProps

const TenantListItem: React.StyledFC<TenantListItemProps> = ({
  className,
  storeName,
  tenantName,
  tenantCode,
  to,
  status,
  trainedAt,
  orgCode,
  infoUpdatedAt,
  contractorCode,
  section,
  representativeRegisterNumber,
}: TenantListItemProps) => {
  const isOpa =
    orgCode && VIEWABLE_CONTRACT_TYPE_AND_REGISTER_ORG_CODES.includes(orgCode)

  return (
    <ListItem
      button
      component={Link}
      classes={{ root: style.listItem }}
      className={className}
      to={to}
    >
      <ListItemIcon classes={{ root: style.tenantIcon }}>
        <Icon icon="tenant" />
      </ListItemIcon>
      <Box className={isOpa ? style.opaContent : style.content}>
        <ListItemText
          className={style.listItemText}
          disableTypography
          secondary={
            <Typography variant="h6">
              <Ellipse
                color={statuses[status].ellipseColor}
                className={style.ellipseIcon}
              />
              <Typography
                className={style[status]}
                variant="inherit"
                component="span"
              >
                {statuses[status].title}
              </Typography>
            </Typography>
          }
        >
          <Box className={style.tenantNameAndCodeContent}>
            <Typography
              className={style.tenantCode}
              variant="subtitle1"
              color="textSecondary"
              component="span"
            >
              {tenantCode}
            </Typography>
            <Typography
              className={style.tenantName}
              variant="subtitle1"
              component="span"
            >
              {tenantName}
            </Typography>
          </Box>
        </ListItemText>
        <Box className={isOpa ? style.opaDescription : style.description}>
          <Typography
            className={clsx(style.store, style.text)}
            variant="h4"
            component="span"
          >
            {storeName}
          </Typography>
          {isOpa && (
            <>
              <Typography className={style.text} variant="h4" component="span">
                {contractorCode}
              </Typography>
              <Typography className={style.text} variant="h4" component="span">
                {section}
              </Typography>
              <Typography className={style.text} variant="h4" component="span">
                {representativeRegisterNumber}
              </Typography>
              <Typography className={style.text} variant="h4" component="span">
                {infoUpdatedAt}
              </Typography>
            </>
          )}
          <Typography component="span" variant="h4" className={style.text}>
            {trainedAt}
          </Typography>
        </Box>
      </Box>
    </ListItem>
  )
}

export default TenantListItem
